import React, { useEffect, useState, useCallback } from 'react';
import { StatusBadge } from '../shared/StatusBadge';
import LoadingSpinner from '../shared/LoadingSpinner';
import { formatDateTime } from '../../utils/dateFormatters';
import {
    TabContent,
    DetailPanel,
    ListDetailLayout,
    ListPanel,
    InlineFieldContainer,
    InlineFieldRow,
    InlineFieldLabel,
    InlineFieldValue,
    TwoColumnLayout,
    HazardIcon,
    HazardIconSelector,
    HazardIconOption,
    EditActionsBar,
} from '../../pages/admin/AdminPage.styles';
import { SafetyHazardItem } from '../../types/SafetyHazardTypes';
import { EditableInput, EditableTextarea } from '../product/EditableFields.styles';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import useUpdateSafetyHazard from '../../api/useUpdateSafetyHazard';

// Available hazard icons (matching old implementation)
const HAZARD_ICONS = [
    'hazard_corrosive.png',
    'hazard_environmental.png',
    'hazard_exclamation.png',
    'hazard_explosion.png',
    'hazard_flammable.png',
    'hazard_health.png',
    'hazard_oxidizer.png',
    'hazard_pressurized.png',
    'hazard_toxic.png',
];

interface SafetyHazardTabProps {
    safetyHazards: SafetyHazardItem[];
    safetyLoading: boolean;
    safetyError: string | null;
    selectedSafetyHazard: SafetyHazardItem | null;
    setSelectedSafetyHazard: (hazard: SafetyHazardItem) => void;
    onRefresh?: () => void;
}

const SafetyHazardTab: React.FC<SafetyHazardTabProps> = ({
    safetyHazards,
    safetyLoading,
    safetyError,
    selectedSafetyHazard,
    setSelectedSafetyHazard,
    onRefresh,
}) => {
    const { updateSafetyHazard, isLoading: isUpdating } = useUpdateSafetyHazard();
    const { isInEditMode, setEditMode } = useEditMode();
    const { loggedInUser } = useAuthStore();
    const [formData, setFormData] = useState<SafetyHazardItem | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);

    const canEdit = loggedInUser !== null;

    // Initialize form data when safety hazard is selected
    useEffect(() => {
        if (selectedSafetyHazard) {
            setFormData(selectedSafetyHazard);
            // Don't reset edit mode if this is a new item (id === 0)
            if (selectedSafetyHazard.id !== 0) {
                setEditMode(false);
            }
        }
    }, [selectedSafetyHazard, setEditMode]);

    // Handle edit mode
    const handleEdit = useCallback(() => {
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode]);

    // Handle add new safety hazard
    const handleAddNew = useCallback(() => {
        const newHazard: SafetyHazardItem = {
            id: 0,
            hazardCode: '',
            category: '',
            hazardDescription: '',
            normalExposureLevel: 0,
            highExposureLevel: 0,
            extremeExposureLevel: 0,
            physicalPropertiesScore: 0,
            healthPropertiesScore: 0,
            environmentPropertiesScore: 0,
            requiresExposureLog: false,
            isActive: true,
            warningImage: '',
            lastUpdatedUtc: new Date().toISOString(),
        };
        setSelectedSafetyHazard(newHazard);
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode, setSelectedSafetyHazard]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        setFormData(selectedSafetyHazard);
        setEditMode(false);
        setUpdateError(null);
    }, [selectedSafetyHazard, setEditMode]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!formData) return;

        try {
            setUpdateError(null);
            await updateSafetyHazard(formData);
            setEditMode(false);
            // Refresh the list to show updated data
            onRefresh?.();
        } catch (error: any) {
            setUpdateError(error.message || 'Failed to update safety hazard');
            console.error('Failed to save safety hazard:', error);
        }
    }, [formData, updateSafetyHazard, setEditMode, onRefresh]);

    // Handle field changes
    const handleFieldChange = useCallback((field: string, value: any) => {
        setFormData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
            };
        });
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!safetyHazards.length) return;

            const currentIndex = safetyHazards.findIndex((h) => h.id === selectedSafetyHazard?.id);

            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    const nextIndex = currentIndex < safetyHazards.length - 1 ? currentIndex + 1 : 0;
                    setSelectedSafetyHazard(safetyHazards[nextIndex]);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : safetyHazards.length - 1;
                    setSelectedSafetyHazard(safetyHazards[prevIndex]);
                    break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [safetyHazards, selectedSafetyHazard, setSelectedSafetyHazard]);
    return (
        <TabContent>
            <ListDetailLayout>
                <ListPanel>
                    {safetyLoading && <LoadingSpinner text="Loading safety hazards..." />}

                    {safetyError && (
                        <div className="error-state">
                            <i className="fas fa-exclamation-triangle"></i> Error loading safety hazards: {safetyError}
                        </div>
                    )}

                    {!safetyLoading && !safetyError && safetyHazards.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-exclamation-triangle"></i>
                            <p>No safety hazards found</p>
                        </div>
                    )}

                    {!safetyLoading && safetyHazards.length > 0 && (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Category</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {safetyHazards.map((hazard) => (
                                        <tr
                                            key={hazard.id}
                                            className={`table-row ${!hazard.isActive ? 'inactive' : ''} ${
                                                selectedSafetyHazard?.id === hazard.id ? 'selected' : ''
                                            }`}
                                            onClick={() => setSelectedSafetyHazard(hazard)}
                                        >
                                            <td>
                                                <a href={`#/safety-hazard-details/${hazard.id}`}>{hazard.hazardCode}</a>
                                            </td>
                                            <td className="category">{hazard.category}</td>
                                            <td className="description">{hazard.hazardDescription}</td>
                                            {/* <td>{hazard.normalExposureLevel}</td>
                                            <td>{hazard.highExposureLevel}</td>
                                            <td>{hazard.extremeExposureLevel}</td>
                                            <td>{hazard.physicalPropertiesScore}</td>
                                            <td>{hazard.healthPropertiesScore}</td>
                                            <td>{hazard.environmentPropertiesScore}</td>
                                            <td>{hazard.requiresExposureLog ? 'Yes' : 'No'}</td>
                                            <td>{hazard.isActive ? 'Yes' : 'No'}</td>
                                            <td>{hazard.warningImage && <img src={`/assets/images/${hazard.warningImage}`} alt="Warning" />}</td> */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListPanel>

                <DetailPanel>
                    {selectedSafetyHazard && formData ? (
                        <>
                            {isInEditMode && (
                                <EditActionsBar>
                                    <button onClick={handleAddNew} className="btn-add-new" title="Add new safety hazard">
                                        <i className="fas fa-plus"></i> New Safety Hazard
                                    </button>
                                    <div>
                                        <button onClick={handleSave} className="btn-save" disabled={isUpdating} title="Save changes">
                                            <i className="fas fa-save"></i> {isUpdating ? 'Saving...' : 'Save'}
                                        </button>
                                        <button onClick={handleCancel} className="btn-cancel" disabled={isUpdating} title="Cancel changes">
                                            <i className="fas fa-times"></i> Cancel
                                        </button>
                                    </div>
                                </EditActionsBar>
                            )}
                            <div className="detail-content">
                                <div className="detail-header">
                                    <h3>
                                        <span className="hazard-code">
                                            {formData.hazardCode} {formData.category}
                                        </span>
                                    </h3>

                                    <div>
                                        <div className="header-actions">
                                            <StatusBadge variant={formData.isActive ? 'active' : 'inactive'} size="medium">
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </StatusBadge>
                                            {canEdit && !isInEditMode && (
                                                <button onClick={handleEdit} className="edit-btn" title="Edit">
                                                    <i className="fas fa-pencil-alt"></i>
                                                </button>
                                            )}
                                        </div>
                                        {formData.lastUpdatedUtc && (
                                            <div className="last-updated">Last updated: {formatDateTime(formData.lastUpdatedUtc)}</div>
                                        )}
                                    </div>
                                </div>
                                {updateError && (
                                    <div className="error-alert">
                                        <i className="fas fa-exclamation-triangle"></i> {updateError}
                                    </div>
                                )}
                                <div className="detail-body">
                                    <div className="detail-section">
                                        <h4>Basic Information</h4>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>Hazard Code</label>
                                                {isInEditMode ? (
                                                    <EditableInput
                                                        value={formData.hazardCode || ''}
                                                        onChange={(e) => handleFieldChange('hazardCode', e.target.value)}
                                                        placeholder="Enter hazard code"
                                                    />
                                                ) : (
                                                    <span>{formData.hazardCode}</span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>Category</label>
                                                {isInEditMode ? (
                                                    <EditableInput
                                                        value={formData.category || ''}
                                                        onChange={(e) => handleFieldChange('category', e.target.value)}
                                                        placeholder="Enter category"
                                                    />
                                                ) : (
                                                    <span>{formData.category}</span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>Active</label>
                                                {isInEditMode ? (
                                                    <select
                                                        value={formData.isActive ? 'true' : 'false'}
                                                        onChange={(e) => handleFieldChange('isActive', e.target.value === 'true')}
                                                        className="edit-select"
                                                        title="Active status"
                                                    >
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                ) : (
                                                    <span>{formData.isActive ? 'Yes' : 'No'}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <TwoColumnLayout>
                                        <div className="detail-section">
                                            <h4>Exposure Levels</h4>
                                            <InlineFieldContainer>
                                                <InlineFieldRow>
                                                    <InlineFieldLabel>Normal exposure:</InlineFieldLabel>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            type="number"
                                                            value={formData.normalExposureLevel ?? 0}
                                                            onChange={(e) => handleFieldChange('normalExposureLevel', parseInt(e.target.value) || 0)}
                                                        />
                                                    ) : (
                                                        <InlineFieldValue>{formData.normalExposureLevel ?? 0}</InlineFieldValue>
                                                    )}
                                                </InlineFieldRow>
                                                <InlineFieldRow>
                                                    <InlineFieldLabel>High exposure:</InlineFieldLabel>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            type="number"
                                                            value={formData.highExposureLevel ?? 0}
                                                            onChange={(e) => handleFieldChange('highExposureLevel', parseInt(e.target.value) || 0)}
                                                        />
                                                    ) : (
                                                        <InlineFieldValue>{formData.highExposureLevel ?? 0}</InlineFieldValue>
                                                    )}
                                                </InlineFieldRow>
                                                <InlineFieldRow>
                                                    <InlineFieldLabel>Extreme exposure:</InlineFieldLabel>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            type="number"
                                                            value={formData.extremeExposureLevel ?? 0}
                                                            onChange={(e) => handleFieldChange('extremeExposureLevel', parseInt(e.target.value) || 0)}
                                                        />
                                                    ) : (
                                                        <InlineFieldValue>{formData.extremeExposureLevel ?? 0}</InlineFieldValue>
                                                    )}
                                                </InlineFieldRow>
                                            </InlineFieldContainer>
                                        </div>
                                        <div className="detail-section">
                                            <h4>Properties Scores</h4>
                                            <InlineFieldContainer>
                                                <InlineFieldRow>
                                                    <InlineFieldLabel>Physical properties:</InlineFieldLabel>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            type="number"
                                                            value={formData.physicalPropertiesScore ?? 0}
                                                            onChange={(e) =>
                                                                handleFieldChange('physicalPropertiesScore', parseInt(e.target.value) || 0)
                                                            }
                                                        />
                                                    ) : (
                                                        <InlineFieldValue>{formData.physicalPropertiesScore ?? 0}</InlineFieldValue>
                                                    )}
                                                </InlineFieldRow>
                                                <InlineFieldRow>
                                                    <InlineFieldLabel>Health properties:</InlineFieldLabel>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            type="number"
                                                            value={formData.healthPropertiesScore ?? 0}
                                                            onChange={(e) =>
                                                                handleFieldChange('healthPropertiesScore', parseInt(e.target.value) || 0)
                                                            }
                                                        />
                                                    ) : (
                                                        <InlineFieldValue>{formData.healthPropertiesScore ?? 0}</InlineFieldValue>
                                                    )}
                                                </InlineFieldRow>
                                                <InlineFieldRow>
                                                    <InlineFieldLabel>Environment properties:</InlineFieldLabel>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            type="number"
                                                            value={formData.environmentPropertiesScore ?? 0}
                                                            onChange={(e) =>
                                                                handleFieldChange('environmentPropertiesScore', parseInt(e.target.value) || 0)
                                                            }
                                                        />
                                                    ) : (
                                                        <InlineFieldValue>{formData.environmentPropertiesScore ?? 0}</InlineFieldValue>
                                                    )}
                                                </InlineFieldRow>
                                            </InlineFieldContainer>
                                        </div>
                                    </TwoColumnLayout>
                                    <div className="detail-section">
                                        <h4>Additional Information</h4>
                                        <div className="detail-grid" style={{ gridTemplateColumns: '70% 30%' }}>
                                            <div className="detail-item">
                                                <label>Warning Image</label>
                                                {isInEditMode ? (
                                                    <HazardIconSelector>
                                                        {HAZARD_ICONS.map((icon) => (
                                                            <HazardIconOption
                                                                key={icon}
                                                                src={`/assets/images/${icon}`}
                                                                alt={icon.replace('.png', '').replace('hazard_', '')}
                                                                $isSelected={formData.warningImage === icon}
                                                                onClick={() => handleFieldChange('warningImage', icon)}
                                                                title={icon.replace('.png', '').replace('hazard_', '').replace('_', ' ')}
                                                            />
                                                        ))}
                                                    </HazardIconSelector>
                                                ) : (
                                                    <span>
                                                        {formData.warningImage ? (
                                                            <HazardIcon src={`/assets/images/${formData.warningImage}`} alt="Warning" />
                                                        ) : (
                                                            'None'
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>Requires exposure log</label>
                                                {isInEditMode ? (
                                                    <select
                                                        value={formData.requiresExposureLog ? 'true' : 'false'}
                                                        onChange={(e) => handleFieldChange('requiresExposureLog', e.target.value === 'true')}
                                                        className="edit-select"
                                                        title="Requires exposure log"
                                                    >
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                ) : (
                                                    <span>{formData.requiresExposureLog ? 'Yes' : 'No'}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="detail-section">
                                        <h4>Description</h4>
                                        {isInEditMode ? (
                                            <EditableTextarea
                                                value={formData.hazardDescription || ''}
                                                onChange={(e) => handleFieldChange('hazardDescription', e.target.value)}
                                                placeholder="Enter description"
                                                rows={4}
                                            />
                                        ) : (
                                            <p>{formData.hazardDescription}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="detail-placeholder">
                            <i className="fas fa-exclamation-triangle"></i>
                            <h3>Select a safety hazard</h3>
                            <p>Choose a safety hazard from the list to view its details</p>
                        </div>
                    )}
                </DetailPanel>
            </ListDetailLayout>
        </TabContent>
    );
};

export default SafetyHazardTab;
